import { t as uiText } from '../i18n';
import React, { useState } from 'react';
import { exportSelectedPages, ExportFormats } from '../api/export';

interface Props {
  workspaceId: string;
  selectedPageIds: string[];
  disabled?: boolean;
}

const ExportSelectedButton: React.FC<Props> = ({ workspaceId, selectedPageIds, disabled }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formats, setFormats] = useState<ExportFormats>({ md: true, json: false, pdf: false, csv: false, csvEncoding: 'utf8' });

  const handleDownload = async () => {
    if (!workspaceId || selectedPageIds.length === 0 || loading) return;
    setLoading(true);
    try {
      const res = await exportSelectedPages(workspaceId, selectedPageIds, formats);
      if (res?.downloadUrl) {
        // 即時ダウンロード
        const a = document.createElement('a');
        a.href = res.downloadUrl;
        a.download = res.filename || 'export.zip';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        alert(uiText("Failed to get the download URL."));
      }
      setOpen(false);
    } catch (e: any) {
      console.error('エクスポート失敗:', e);
      alert(e?.message || uiText("An error occurred during export"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        disabled={disabled || selectedPageIds.length === 0 || loading}
        style={{
          padding: '6px 12px',
          backgroundColor: disabled || selectedPageIds.length === 0 || loading ? '#ccc' : '#6f42c1',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: disabled || selectedPageIds.length === 0 || loading ? 'not-allowed' : 'pointer',
          fontSize: '12px',
          marginLeft: '8px'
        }}
      >
        {loading ? uiText("Getting ready...") : uiText("Download")}
      </button>

      {open && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => !loading && setOpen(false)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '24px',
              borderRadius: '8px',
              minWidth: '320px',
              maxWidth: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>{uiText("Select an export format")}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={formats.md}
                  onChange={(e) => setFormats((f) => ({ ...f, md: e.target.checked }))}
                />
                <span>Markdown (.md) </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={!!formats.json}
                  onChange={(e) => setFormats((f) => ({ ...f, json: e.target.checked }))}
                />
                <span>{uiText("JSON (includes each page JSON in addition to metadata.ndjson)")}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={!!formats.csv}
                  onChange={(e) => setFormats((f) => ({ ...f, csv: e.target.checked }))}
                />
                <span>{uiText("CSV (pages.csv: detailed metadata list)")}</span>
              </label>
              {formats.csv && (
                <div style={{ marginLeft: '24px', display: 'flex', gap: '16px', fontSize: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="csvEncoding"
                      value="utf8"
                      checked={formats.csvEncoding === 'utf8'}
                      onChange={() => setFormats((f) => ({ ...f, csvEncoding: 'utf8' }))}
                    />
                    <span>{uiText("UTF-8 (with BOM)")}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="csvEncoding"
                      value="sjis"
                      checked={formats.csvEncoding === 'sjis'}
                      onChange={() => setFormats((f) => ({ ...f, csvEncoding: 'sjis' }))}
                    />
                    <span>{uiText("Shift-JIS (Excel compatible)")}</span>
                  </label>
                </div>
              )}

            </div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
              {uiText("- Images and attachments are not supported yet (links are kept as-is)")}<br />
              {uiText("- Includes metadata.ndjson / mapping.csv / index.md")}<br />
              {uiText("- Downloaded as a ZIP file")}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setOpen(false)}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ccc',
                  color: '#000',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {uiText("Cancel")}
              </button>
              <button
                onClick={handleDownload}
                disabled={loading || (!formats.md && !formats.json && !formats.csv)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: loading || (!formats.md && !formats.json && !formats.csv) ? '#ccc' : '#6f42c1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading || (!formats.md && !formats.json && !formats.csv) ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? uiText("Getting ready...") : uiText("Download {{length}} selected items", { length: selectedPageIds.length })}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExportSelectedButton;



